import { fromBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";
import type {
  AdditionalService,
  AdditionalServiceVoRaw,
  CancelServiceInput,
  EditServiceInput,
  PreOrder,
  PreOrderItem,
  PreOrderItemRaw,
  PreOrderVoRaw,
  ServiceType,
} from "../types/value-service";
import type { LangOpts } from "./product";

/** Backend AdditionalServiceTypeEnum: 1 = PHOTO, 2 = VIDEO. */
export const SERVICE_TYPE_PHOTO: ServiceType = 1;
export const SERVICE_TYPE_VIDEO: ServiceType = 2;
/** Backend video sub-type. Only `1` (footwear video) exists today. */
export const VIDEO_TYPE_DEFAULT = 1;

const toKopecks = (n: number | null | undefined): number => Math.round((n ?? 0) * 100);
const toKopecksOrNull = (n: number | null | undefined): number | null =>
  n == null ? null : Math.round(n * 100);

function normalizeService(
  raw: AdditionalServiceVoRaw,
  fallbackType: ServiceType,
): AdditionalService {
  return {
    id: raw.id ?? null,
    serviceType: (raw.serviceType as ServiceType) ?? fallbackType,
    imageUrl: raw.imageUrl ?? raw.serviceUrl ?? null,
    videoType: raw.videoType ?? null,
    priceRubKopecks: toKopecks(raw.price),
    serviceRemark: raw.serviceRemark ?? null,
  };
}

function normalizePreOrderItem(raw: PreOrderItemRaw): PreOrderItem {
  return {
    id: raw.id,
    spuNo: raw.spuNo,
    channel: fromBackendChannel(raw.channel),
    title: raw.subject,
    imageUrl: raw.mainImg,
    quantity: raw.quantity ?? 1,
    skuRemarks: raw.skuRemarks,
    shopName: raw.shopName,
    priceRubKopecks: toKopecks(raw.price),
    totalPriceRubKopecks: toKopecks(raw.totalPrice ?? raw.price * (raw.quantity ?? 1)),
    photoServices: (raw.services ?? []).map((s) => normalizeService(s, SERVICE_TYPE_PHOTO)),
    videoServices: (raw.videoServicesV1 ?? []).map((s) => normalizeService(s, SERVICE_TYPE_VIDEO)),
    photoUnitPriceRubKopecks: toKopecksOrNull(raw.photoUnitPrice),
    videoUnitPriceRubKopecks: toKopecksOrNull(raw.videoUnitPrice),
    photoServicePriceRubKopecks: toKopecks(raw.photoServicePrice),
    videoServicePriceRubKopecks: toKopecks(raw.videoServicePrice),
  };
}

/**
 * POST /order/generatePreOrderNo — turns the picked cart-item ids into a
 * temporary pre-order number. Backend signature is `@RequestBody List<String>`
 * so we send a bare JSON array (same convention as /shopcart/deleteBatch).
 */
export async function generatePreOrderNo(
  ctx: ApiFetchContext,
  authToken: string,
  cartItemIds: string[],
): Promise<string> {
  return apiFetch<string>(ctx, "/order/generatePreOrderNo", {
    method: "POST",
    authToken,
    body: cartItemIds,
  });
}

/**
 * GET /order/pre/order?preOrderNo=… — authoritative pre-order snapshot. ALL
 * money (item totals, domestic freight, value-add service fee, grand total)
 * is computed server-side here; the client never recomputes it.
 */
export async function queryPreOrder(
  ctx: ApiFetchContext,
  authToken: string,
  preOrderNo: string,
  opts: LangOpts = {},
): Promise<PreOrder> {
  const raw = await apiFetch<PreOrderVoRaw>(
    ctx,
    `/order/pre/order?preOrderNo=${encodeURIComponent(preOrderNo)}`,
    opts.lang === undefined ? { authToken } : { authToken, lang: opts.lang },
  );
  const items = (raw.shopCartList ?? []).flatMap((g) =>
    (g.itemList ?? []).map(normalizePreOrderItem),
  );
  return {
    preOrderNo,
    items,
    additionalServiceFeeRubKopecks: toKopecks(raw.additionalServiceFee),
    totalExpressFeeRubKopecks: toKopecks(raw.totalExpressFee),
    totalFeeRubKopecks: toKopecks(raw.totalFee),
    discountFeeRubKopecks: toKopecks(raw.discountFee),
  };
}

/**
 * POST /shopcart/edit/service — sets the value-added services on ONE cart
 * item. Sending empty arrays clears that service category. The fee change is
 * reflected only after re-reading the pre-order.
 */
export async function editCartService(
  ctx: ApiFetchContext,
  authToken: string,
  input: EditServiceInput,
): Promise<void> {
  // Backend returns a bare `Result.OK()` (no payload) — `allowEmptyData` keeps
  // the null result from throwing BAD_ENVELOPE, which would block the
  // post-edit pre-order refetch and freeze the summary total (#261).
  await apiFetch<void>(ctx, "/shopcart/edit/service", {
    method: "POST",
    authToken,
    allowEmptyData: true,
    body: {
      id: input.id,
      services: input.services ?? [],
      videoServices: input.videoServices ?? [],
    },
  });
}

/** POST /shopcart/cancel/service — drops a whole service category from a cart item. */
export async function cancelCartService(
  ctx: ApiFetchContext,
  authToken: string,
  input: CancelServiceInput,
): Promise<void> {
  // Same empty-envelope contract as editCartService (#261).
  await apiFetch<void>(ctx, "/shopcart/cancel/service", {
    method: "POST",
    authToken,
    allowEmptyData: true,
    body: { id: input.id, serviceType: input.serviceType },
  });
}

/**
 * POST /sys/common/upload — multipart reference-image upload (老系统 commonUpload).
 * Returns the stored file URL to feed into a value-add service's `imageUrl`.
 */
export async function uploadServiceImage(
  ctx: ApiFetchContext,
  authToken: string,
  file: File,
): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  form.append("biz", "custom");
  const res = await apiFetch<{ url?: string; message?: string } | string>(
    ctx,
    "/sys/common/upload",
    { method: "POST", authToken, body: form },
  );
  const url = typeof res === "string" ? res : res.url;
  if (!url) {
    throw new Error("Upload succeeded but no URL was returned");
  }
  return url;
}
